import { env } from "@/lib/env";
import { useToasts } from "@/stores/toast";

/**
 * Registers the app-shell service worker in production builds (dev keeps Vite's live reload), marks
 * <html> with `is-standalone` when running as the installed app, and offers a refresh when a new
 * version has taken over (no one is left on a stale bundle after a deploy).
 */
export function registerServiceWorker(): void {
  if (typeof window === "undefined") return;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (standalone) document.documentElement.classList.add("is-standalone");

  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  const hadController = !!navigator.serviceWorker.controller;
  let notified = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || notified) return; // first install: nothing to refresh
    notified = true;
    useToasts.getState().push({
      title: "RealityStore was updated",
      description: "Refresh to get the newest version.",
      tone: "info",
      duration: 60_000,
      action: { label: "Refresh", onClick: () => window.location.reload() },
    });
  });
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // The worker re-registers rotated push subscriptions itself; it needs the API address.
        const tell = () => (reg.active ?? reg.waiting ?? reg.installing)?.postMessage({ type: "config", api: env.apiBaseUrl });
        if (reg.active) tell();
        else void navigator.serviceWorker.ready.then(tell);
        // Look for a new version whenever the app comes back to the foreground.
        document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && void reg.update());
      })
      .catch(() => {
        /* the site works without it */
      });
  });
}
